import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { Box, Divider, FormControlLabel } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { TextField } from 'common/components/TextField';
import { useSetUserPasswordMutation } from 'graphql/types';
import { FormField } from 'common/components/FormField';
import { Button } from 'common/components/buttons/Button';
import { Checkbox } from 'common/components/Checkbox';
import { useSnackbarContext } from 'common/context';
import { validators } from 'common/constants';
import { Link } from 'common/components/Link';

type TInitialValues = {
    password: string;
    passwordConfirm: string;
    termsAgreed: boolean;
};

interface IProps {
    className?: string;
    uid: string;
    token: string;
    onSubmit(): void;
}

const StyledFormControlLabel = styled(FormControlLabel)`
    height: 100px;
    margin-left: ${({ theme }) => `${theme.spacing(1)}px`};

    ${Checkbox} {
        width: 20px;
        height: 20px;
        border: 0;
        border-radius: 0;
        margin-right: ${({ theme }) => `${theme.spacing(4)}px`};

        .MuiFormControlLabel-label {
            font-size: 1rem;
        }

        .MuiSvgIcon-root {
            font-size: 2.5rem;
        }
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['sm']}px`}) {
        height: 50px;
    }
`;

const BaseCreatePasswordForm = ({
    className,
    uid,
    token,
    onSubmit,
}: IProps) => {
    const { t } = useTranslation();
    const { setSnackbarState } = useSnackbarContext();
    const [mutatePassword] = useSetUserPasswordMutation({
        update: () => {
            onSubmit();
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const formik = useFormik({
        initialValues: {
            password: '',
            passwordConfirm: '',
            termsAgreed: false,
        },
        validationSchema: Yup.object().shape({
            password: validators.password,
            passwordConfirm: Yup.string()
                .required(t('validators.passwordConfirm.required'))
                .oneOf(
                    [Yup.ref('password')],
                    t('validators.passwordConfirm.invalid')
                ),
        }),
        onSubmit: (values) => {
            mutatePassword({
                variables: {
                    uid,
                    token,
                    isInitialSignup: true,
                    userData: {
                        password: values.password,
                    },
                },
            });
        },
    });

    const {
        values,
        errors: formikErrors,
        touched,
        handleChange,
        handleSubmit,
    } = formik;

    const getFieldError = (fieldName: keyof TInitialValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    return (
        <Box className={className}>
            <form onSubmit={handleSubmit}>
                <Box maxWidth="455px">
                    <FormField>
                        <TextField
                            error={getFieldError('password')}
                            id="password"
                            label={t('formfields.password')}
                            type="password"
                            value={values.password}
                            variant="filled"
                            onChange={handleChange}
                        />
                    </FormField>

                    <FormField>
                        <TextField
                            error={getFieldError('passwordConfirm')}
                            id="passwordConfirm"
                            label={t('formfields.passwordConfirm')}
                            type="password"
                            value={values.passwordConfirm}
                            variant="filled"
                            onChange={handleChange}
                        />
                    </FormField>
                </Box>

                <FormField>
                    <StyledFormControlLabel
                        control={
                            <Checkbox
                                disableRipple
                                checked={values.termsAgreed}
                                color="primary"
                                name="termsAgreed"
                                onChange={handleChange}
                            />
                        }
                        label={
                            <>
                                {t('account.setPassword.terms_1')}{' '}
                                <Link
                                    href="https://brilvandebaas.nl/algemene-voorwaarden.html"
                                    target="_blank"
                                    to=""
                                >
                                    {t('account.setPassword.terms_2')}
                                </Link>{' '}
                                {t('account.setPassword.terms_3')}{' '}
                                <Link
                                    href="https://brilvandebaas.nl/privacy.html"
                                    target="_blank"
                                    to=""
                                >
                                    {t('account.setPassword.terms_4')}
                                </Link>
                            </>
                        }
                    />
                </FormField>

                <Box mb={8} mt={8}>
                    <Divider />
                </Box>

                <Box className="buttonWrapper">
                    <Button
                        fullWidth
                        disabled={!values.termsAgreed}
                        onClick={() => handleSubmit()}
                    >
                        {t('account.setPassword.submit')}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export const CreatePasswordForm = styled(BaseCreatePasswordForm)`
    display: flex;

    .buttonWrapper {
        width: 100%;
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['sm']}px`}) {
        .buttonWrapper {
            max-width: 455px;
        }
    }
`;
